import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteDelete } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { assign, get, partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteContext from "./default-class-website-context"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
}

export default function deleteClassWebsiteAction(payload: Payload): PreparedAction {
  return {
    name: "DELETE_CLASS_WEBSITE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let websitePatch = Patch.create("class-website", payload.websiteId).set(
    "attributes.isDeleted",
    true
  ).patch

  let planbookPatch = Patch.create("planbook", payload.planbookId).pull(
    "relationships.classWebsites.data",
    {
      id: payload.websiteId,
    }
  ).patch

  return [websitePatch, planbookPatch]
}

function undoPatches(payload: Payload) {
  let websitePatch = Patch.create("class-website", payload.websiteId).set(
    "attributes.isDeleted",
    false
  ).patch

  let planbookPatch = Patch.create("planbook", payload.planbookId).push(
    "relationships.classWebsites.data",
    {
      id: payload.websiteId,
    }
  ).patch

  return [websitePatch, planbookPatch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let websiteTitle = get(models, "classWebsite.attributes.title")
  let websiteSlug = get(models, "classWebsite.attributes.slug")
  let context = assign({}, defaultClassWebsiteContext(payload, models), {
    websiteTitle,
    websiteSlug,
  })
  let newEvent: ClassWebsiteDelete = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "delete",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: null,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],
  }

  return {
    title: "Deleted Class Website",
    titleForAnalytics: "Class Website - Deleted",
    activity: defaultClassWebsiteActivity(payload, models, "removed"),
    context,
    description: `Deleted your "${websiteTitle}" class website`,
    newAnalyticsEvents: [newEvent],
  }
}
